// Registra Service Worker do PWA (vite-plugin-pwa) em produção.
// Em dev, MSW assume o SW pra interceptar fetch — PWA SW desabilitado pra evitar conflito.

// Intervalo do update proativo do SW. Encurta a janela entre um deploy novo e
// a troca do SW velho pelo novo: registration.update() força o browser a
// rebuscar o sw.js (nginx serve no-cache no scope do SW) e, se mudou,
// dispara install→skipWaiting→activate→clients.claim→controllerchange→reload
// (cadeia do autoUpdate). 15min é barato (1 GET condicional, normalmente 304)
// e não vira fetch abusivo (custo conferido com infra-perf no gate).
const SW_UPDATE_INTERVAL_MS = 15 * 60 * 1000

// Flag idempotente do unregister proativo em vlow. Evita tentar unregister em
// loop a cada boot. localStorage (não session) pra valer entre aberturas do app.
const VLOW_UNREGISTER_FLAG = 'iptv:sw-vlow-unregistered'

/**
 * Migra um device vlow que ficou PRESO num SW velho buggy.
 *
 * A regra "vlow desabilita SW" (Tizen 3.x / webOS 3.x = TVs 2017, SW quebrado)
 * só impedia REGISTRAR um SW novo — mas não removia um SW que o device já tinha
 * pego num tier mais alto antes (override de tier, heurística que mudou, etc).
 * Resultado: vlow podia ficar eternamente preso num SW velho sem migrar.
 *
 * Unregister proativo é SEGURO: é exatamente o que runRecovery já faz em prod
 * em todos os tiers, sem crash reportado. O ÚNICO risco conhecido é reloadar
 * DENTRO do lifecycle do SW (Tizen 2017 trava — comentário em sw-recovery.ts).
 * Por isso aqui NÃO reloadamos: só unregister no boot. O app já está rodando
 * (servido local/nginx); na próxima abertura o SW velho não está mais lá e o
 * index vem fresh. Idempotente por flag pra não tentar a cada boot.
 */
export async function unregisterStaleSwForVlow(): Promise<void> {
  try {
    if (localStorage.getItem(VLOW_UNREGISTER_FLAG) === '1') return
  } catch {
    // storage bloqueado — segue, o unregister é best-effort idempotente mesmo
  }
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    if (regs.length > 0) {
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
      console.warn(`[sw] vlow: unregister proativo de ${regs.length} SW(s) velho(s)`)
    }
  } catch (err) {
    console.warn('[sw] vlow: unregister falhou (ignorado)', err)
  } finally {
    // Marca como feito MESMO se não havia SW: o objetivo (não ficar preso) está
    // garantido e não precisamos repetir a varredura todo boot. Sem reload aqui
    // de propósito (Tizen 2017 trava reload no lifecycle do SW).
    try {
      localStorage.setItem(VLOW_UNREGISTER_FLAG, '1')
    } catch {
      // ignora — pior caso roda de novo no próximo boot, ainda inócuo
    }
  }
}

export async function registerServiceWorker(): Promise<void> {
  if (typeof window === 'undefined') return
  if (!('serviceWorker' in navigator)) return
  // No-op fora de http(s): os wrappers Tizen/webOS carregam o index via
  // file:// e NÃO servem sw.js. `'serviceWorker' in navigator` NÃO protege —
  // o Chromium do webOS trata file:// como secure context e expõe a API, mas
  // `.register()` lança ("URL protocol of the current origin ('file://') is
  // not supported"). Sem esta guarda o boot em TV geraria um erro (e acionaria
  // o helper autoUpdate à toa). SW é web-only por design; aqui saímos limpo.
  if (
    typeof location !== 'undefined' &&
    location.protocol !== 'http:' &&
    location.protocol !== 'https:'
  ) {
    return
  }
  if (import.meta.env.DEV) return // MSW dono do SW em dev
  // Tizen 3.x e webOS 3.x têm SW quebrado — NÃO registra SW novo em vlow.
  // Mas, em vez de só dar return, MIGRA quem ficou preso num SW velho:
  // unregister proativo (sem reload — Tizen 2017 trava reload no lifecycle).
  if (document.documentElement.dataset['tier'] === 'vlow') {
    void unregisterStaleSwForVlow()
    return
  }

  try {
    const { registerSW } = await import('virtual:pwa-register')
    // Em registerType:'autoUpdate' (vite.config.ts) + immediate:true + o
    // skipWaiting()/clients.claim() do nosso sw.ts custom (injectManifest), a
    // troca já é automática: SW novo → skipWaiting → activate → clients.claim →
    // controllerchange → o helper recarrega a página sozinho. Expulsa o SW
    // velho na PRIMEIRA visita pós-deploy, sem esperar todas as abas fecharem.
    // Reload silencioso (TV-first: sem exigir interação por remoto).
    const updateSW = registerSW({
      immediate: true,
      // Cinto-suspensório: em autoUpdate o helper normalmente não chama este
      // callback (é o modo 'prompt' que usa), mas se a estratégia mudar pra
      // prompt no futuro, ativamos o SW novo na hora em vez de exibir prompt.
      onNeedRefresh() {
        void updateSW(true)
      },
      onRegisteredSW(_swUrl, registration) {
        if (!registration) return
        // Update proativo periódico: encurta a janela deploy→troca de SW.
        // registration.update() é um GET condicional do sw.js (304 quando não
        // mudou) — barato. setInterval em vez de a-cada-navegação pra não
        // acoplar ao router e não disparar em rajada (custo conferido c/ infra).
        setInterval(() => {
          void registration.update().catch(() => {
            // offline / rede ruim — tenta no próximo tick, sem ruído
          })
        }, SW_UPDATE_INTERVAL_MS)
      },
      onRegisterError: (err) => console.warn('[sw] register error', err),
    })
  } catch (err) {
    console.warn('[sw] not available', err)
  }
}
